import { Formik, Form } from "formik";
import * as Yup from "yup";
import { IUserLoginOutput } from "@types";
import auth from "@api/auth";
import { getCookie } from "@utils/Cookies";
import { COOKIE_LOGIN_TOKEN_KEY } from "@utils/constants";
import { useAppContext } from "@context/app";
import { useRouter } from "next/router";
import Button from "@elements/button";
import TextInput from "@elements/fields/textInput";
import { useTranslations } from "next-intl";
import SocialLogin from "@modules/auth/socialLogin";
import { segmentEvent, segmentGetAnonymousId, setSegmentIdentifyPhoneNumber } from "@utils/segment";
import { setExternalUserId } from "@utils/oneSignal";

interface LoginFormProps {
  onSwitchView: (view: string) => void;
  onClose: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSwitchView, onClose }) => {
  const { setUserLoggedIn, country, area } = useAppContext();
  const { locale, pathname, reload } = useRouter();
  const t = useTranslations();
  let fromPage: string;
  if (pathname === "/cart") {
    fromPage = "cart";
  } else if (pathname === "/product/[slug]") {
    fromPage = "product_details";
  } else {
    fromPage = "menu";
  }

  const loginValues: IUserLoginOutput = { email: "", password: "" };

  const LoginSchema = Yup.object().shape({
    password: Yup.string().required(t("client_errors_fieldisrequiered")).min(8, (t("client_errors_minchar")).replace("@", "8")),
    email: Yup.string().min(1, (t("client_errors_minchar")).replace("@", "1")).required(t("client_errors_fieldisrequiered")),
  });

  const handelLogin = async (value: IUserLoginOutput, actions: any) => {
    try {
      const res = await auth.login(value).fetch();
      getCookie().set(COOKIE_LOGIN_TOKEN_KEY, res.data.access_token, {}, 365);
      setExternalUserId(`${res.data.user.id}`, res.data.user.os_id_hash)
      setSegmentIdentifyPhoneNumber(res.data.user, segmentGetAnonymousId())
      const eventObject = {
        user_id: res.data.user.id || -1,
        action: "Normal",
        language: locale?.includes("en") ? "English" : "Arabic",
        country_id: country?.id,
        country_slug: country?.slug,
        area_id: area?.id,
        area_slug: area?.slug,
        from_page: fromPage,
        from_entity: "",
        from_entity_slug: "",
        from_page_section: "",
        from_page_section_entity: "",
        from_page_section_slug: "",
        social_login: "",
        is_by_phone: res.data.is_by_phone,
        phone_number: res.data.is_by_phone ? value.email : ""
      }
      segmentEvent("User Logged In", eventObject);
      setUserLoggedIn({ isLoggedIn: true });
      reload();
      onClose();
    } catch (error) {
      console.log(error);
    }
    actions.setSubmitting(false);
  };

  return (
    <div className="w-full mt-14" >
      <Formik
        initialValues={loginValues}
        validationSchema={LoginSchema}
        onSubmit={(values, actions) => {
          handelLogin(values, actions);
        }}
      >
        {({ isSubmitting, isValid, dirty, handleBlur, handleChange, values, errors, touched }) => (
          <Form className="flex flex-col max-w-[320px] w-full m-auto form">
            <div className="w-full mb-6">
              <TextInput
                error={errors.email && touched.email ? errors.email : ""}
                value={values.email}
                name="email"
                label={t("client_general_emailorphone")}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className="w-full mb-1">
              <TextInput
                error={errors.password && touched.password ? errors.password : ""}
                value={values.password}
                name="password"
                label={t("client_general_password")}
                isPassword
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className={`w-full mb-6  pl-1 ${locale?.includes("ar") ? "text-right" : "text-left"}`}>
              <p
                role="button"
                className="text-[#008AF6] font-normal text-xs"
                onClick={() => onSwitchView("forgotPass")}
              >
                {t("client_loginsignup_forgotpassword")}
              </p>
            </div>
            <Button
              className="h-12 shadow-md font-extrabold text-base mb-2 rounded-full"
              size="slim"
              type="submit"
              theme="blue"
              isDisabled={isSubmitting || !isValid || !dirty}
              loading={isSubmitting}
            >
              {t("client_buttons_login")}
            </Button>
            <div className=" w-full mb-3 mt-2">
              <p className=" text-center text-[#808080] text-sm mb-3">{t("client_general_or")}</p>
              <SocialLogin action="login" />
            </div>
            <div className="w-full mb-2 " dir={locale?.includes("ar-") ? "rtl" : "ltr"}>
              <span className="text-[#999] font-normal text-sm">
                {t("w-dontHaveAnAccount")}
              </span>
              <button
                onClick={() => onSwitchView("signUp")}
                className="text-[#008AF6] font-normal text-xs px-1"
              >
                {t('client_buttons_signup')}
              </button>
            </div>
            {(pathname.includes("cart") || pathname.includes("product/[slug]")) && (
              <div className="w-full mt-2 ">
                <button
                  onClick={() => onSwitchView("guest")}
                  className="text-[#008AF6] font-normal text-sm"
                >
                  {t("client_buttons_continueasguest")}
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default LoginForm;